import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

// Campo de texto con sugerencias de lugares de todo el mundo mientras se
// escribe, para ciudades y direcciones de las publicaciones. Sigue siendo
// texto libre: si no se elige ninguna sugerencia queda lo que se tipeó.
//
// mode="city": solo ciudades ("Rosario, Provincia de Santa Fe, Argentina"),
// con la API de geocoding de Open-Meteo: gratis, rápida, en español y
// entiende palabras a medio escribir ("rosar" → Rosario).
// mode="address": calles, lugares y direcciones, con Nominatim (el mismo
// servicio que utils/geo.js), que necesita palabras completas.
//
// No se usa Photon (photon.komoot.io), que también autocompleta: su servidor
// público llegó a tardar ~17 s por consulta y el campo quedaba cargando.
//
// onChange(texto) en cada cambio; onSelect(opción) opcional al elegir una
// sugerencia ({ label, latitude, longitude }, y en mode="address" también
// `city`), por si se quieren guardar las coordenadas o la ciudad. El resto de las props (label, required, error, helperText,
// margin, name...) van al TextField.

// Se espera a que el usuario deje de tipear antes de buscar, para no
// mandar una consulta por cada letra al servicio público.
const DEBOUNCE_MS = 350;
const MIN_CHARS = 3;
// Si el servicio no contesta en este tiempo se corta, para que el campo no
// quede con el "cargando" para siempre.
const TIMEOUT_MS = 8000;

const joinUnique = (parts) => [...new Set(parts.filter(Boolean))].join(", ");

const searchCities = async (text, signal) => {
  const params = new URLSearchParams({ name: text, count: "8", language: "es", format: "json" });
  const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?${params}`, { signal });
  const data = await res.json();
  return (data.results || []).map((r) => ({
    label: joinUnique([r.name, r.admin1, r.country]),
    latitude: r.latitude,
    longitude: r.longitude,
  }));
};

// "Avenida Corrientes 1000, San Nicolás, Buenos Aires, Argentina" en vez del
// display_name completo (con comuna, código postal, etc.), y la ciudad aparte
// para guardarla en el campo `city` que usan los filtros de autos/propiedades.
const searchAddresses = async (text, signal) => {
  const params = new URLSearchParams({ format: "json", q: text, limit: "6", addressdetails: "1" });
  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
    headers: { "Accept-Language": "es" },
    signal,
  });
  const data = await res.json();
  return data.map((item) => {
    const a = item.address || {};
    const city = a.city || a.town || a.village || a.municipality || a.county || "";
    const street = [a.road || a.pedestrian || a.footway, a.house_number].filter(Boolean).join(" ");
    return {
      label: joinUnique([street || item.name, a.suburb || a.neighbourhood, city, a.state, a.country]) || item.display_name,
      city,
      latitude: parseFloat(item.lat),
      longitude: parseFloat(item.lon),
    };
  });
};

const searchLocations = async (text, mode) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const results = await (mode === "city" ? searchCities : searchAddresses)(text, controller.signal);
    const seen = new Set();
    return results
      .filter((opt) => opt.label && !seen.has(opt.label) && seen.add(opt.label))
      .slice(0, 6);
  } finally {
    clearTimeout(timeout);
  }
};

// Los diálogos de edición (editCars/editTrips) usan zIndex 20000 para tapar
// la barra de abajo; la lista de sugerencias tiene que quedar por encima o
// se abre detrás del diálogo y no se ve.
const POPPER_Z_INDEX = 21000;

const LocationAutocompleteField = ({ mode = "address", value, onChange, onSelect, placeholder, id, ...textFieldProps }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const latestQuery = useRef("");

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleInputChange = (event, newValue, reason) => {
    onChange(newValue);
    // "reset" es cuando MUI rellena el texto al elegir una opción: ahí no
    // hay que volver a buscar.
    if (reason !== "input") return;
    clearTimeout(timer.current);
    latestQuery.current = newValue;
    if (newValue.trim().length < MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const results = await searchLocations(newValue.trim(), mode);
        // Si mientras tanto se siguió escribiendo, esta respuesta ya no sirve.
        if (latestQuery.current === newValue) setOptions(results);
      } catch (error) {
        console.warn("No se pudieron obtener sugerencias de ubicación:", error);
      } finally {
        if (latestQuery.current === newValue) setLoading(false);
      }
    }, DEBOUNCE_MS);
  };

  const handleSelect = (event, option) => {
    if (option && typeof option === "object") {
      onChange(option.label);
      onSelect?.(option);
    }
  };

  return (
    <Autocomplete
      id={id}
      freeSolo
      fullWidth
      slotProps={{ popper: { sx: { zIndex: POPPER_Z_INDEX } } }}
      filterOptions={(opts) => opts}
      options={options}
      getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
      isOptionEqualToValue={(opt, val) => opt.label === val.label}
      getOptionKey={(opt) => (typeof opt === "string" ? opt : `${opt.label}-${opt.latitude}-${opt.longitude}`)}
      loading={loading}
      inputValue={value || ""}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          placeholder={placeholder || (mode === "city" ? "Empezá a escribir una ciudad..." : "Empezá a escribir una dirección...")}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default LocationAutocompleteField;
